import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ApplicationSubmissionData } from '../../services/applicationService';
import { UserProfile } from '../../types/user';
import { logger } from '../../utils/logger';
import { fetchImageAsFile } from '../../utils/fileUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import PhotoUpload from '../common/PhotoUpload';
import '../../styles/ApplicationForm.css';

interface ApplicationFormProps {
  onSubmit: (formData: ApplicationSubmissionData, photoFile?: File) => void;
  loading: boolean;
  eventId: string;
  disabled?: boolean; // Add disabled prop to prevent form submission for unverified users
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ onSubmit, loading, eventId, disabled = false }) => {  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [understandingConsent, setUnderstandingConsent] = useState('');
  const [consentAcknowledgment, setConsentAcknowledgment] = useState(false);
  const [dataRetentionAgreement, setDataRetentionAgreement] = useState(false);
  const [attendeeType, setAttendeeType] = useState<'single' | 'couple'>('single');
  const [partnerAlias, setPartnerAlias] = useState('');  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photoPrefillError, setPhotoPrefillError] = useState<string | null>(null);
  
  // Track which fields were auto-filled
  const [autoFilledFields, setAutoFilledFields] = useState<Record<string, boolean>>({});
  const [syncWithProfile, setSyncWithProfile] = useState<boolean>(true);

  const { currentUser, userProfile, loading: authLoading, updateExtendedProfile } = useAuth();
  
  // Logging user info for debugging purposes
  useEffect(() => {
    if (currentUser) {
      logger.debug('👤 Current user available for form auto-fill:', currentUser.email);
    }
    if (userProfile) {
      logger.debug('🧩 User profile available for form auto-fill:', userProfile.firstName || userProfile.username);
    }
  }, [currentUser, userProfile]);

  // Auto-fill the form fields when user data is available
  useEffect(() => {
    const attemptAutoFill = async () => {
      // Only try to auto-fill if we're not already loading and we have user data
      if (!authLoading) {
        
        if (currentUser) {          logger.info('✨ Auto-filling user details in event application form', {
            email: currentUser.email,
            realName: userProfile?.firstName || 'not available'
          });
          
          // Track auto-filled fields
          const filledFields: Record<string, boolean> = {};
            // Auto-fill name from user profile real name
          const realName = userProfile?.firstName || '';
          if (realName) {
            setName(realName);
            filledFields.name = true;
            logger.debug('✨ Auto-filled name field with real name:', realName);
          }
          
          // Auto-fill email from auth data
          const userEmail = currentUser.email || '';
          if (userEmail) {
            setEmail(userEmail);
            filledFields.email = true;
            logger.debug('✨ Auto-filled email field with:', userEmail);
          }
          
          // Auto-fill consent understanding from user profile
          const profileConsentUnderstanding = userProfile?.consentUnderstanding || '';
          if (profileConsentUnderstanding) {
            setUnderstandingConsent(profileConsentUnderstanding);
            filledFields.understandingConsent = true;
            logger.debug('✨ Auto-filled consent understanding from profile');
          }
          
          // Auto-fill introduction from user profile
          const profileIntroduction = userProfile?.introduction || '';
          if (profileIntroduction) {
            setIntroduction(profileIntroduction);
            filledFields.introduction = true;
            logger.debug('✨ Auto-filled introduction from profile');
          }
          
          // Auto-fill photo - prioritize first photo from photoUrls array over profile picture
          const mainPhotoUrl = userProfile?.photoUrls?.[0]; // Get the main photo (first in array)
          const fallbackPhotoUrl = userProfile?.profilePictureUrl || currentUser.photoURL;
          const photoToUse = mainPhotoUrl || fallbackPhotoUrl;
          
          if (photoToUse) {
            const photoSource = mainPhotoUrl 
              ? 'Main Gallery Photo' 
              : (userProfile?.profilePictureUrl ? 'Profile Picture' : 'Firebase Auth');
            
            logger.info('🖼️ Found user photo for auto-fill:', {
              source: photoSource,
              url: photoToUse
            });
            
            try {
              const photoDetails = await fetchImageAsFile(photoToUse, 'profilePhoto.jpg');
              if (photoDetails) {
                setPhotoFile(photoDetails.file);
                setPhotoPreview(photoDetails.previewUrl);
                setPhotoPrefillError(null); // Clear any previous error
                filledFields.photo = true;
                logger.success('✨ Successfully auto-filled photo from user ' + photoSource.toLowerCase());
              } else {
                logger.warn('⚠️ Could not load user photo for auto-fill. User will need to upload manually.');
                setPhotoFile(null);
                setPhotoPreview(null);
                setPhotoPrefillError("We couldn't load your photo. Please upload one manually.");
              }
            } catch (error) {
              logger.error('❌ Error fetching user photo for auto-fill:', error);
              setPhotoFile(null);
              setPhotoPreview(null);
              setPhotoPrefillError("Error loading your photo. Please upload one manually.");
            }
          } else {
            logger.info('ℹ️ No user photos found for auto-fill');
            setPhotoFile(null);
            setPhotoPreview(null);
            setPhotoPrefillError(null); // No photo to prefill, so no error
          }
            // Only reset fields that require explicit acknowledgment each time
          setAttendeeType('single'); // Default to single
          setPartnerAlias('');
          setConsentAcknowledgment(false); // User must re-acknowledge
          setDataRetentionAgreement(false); // User must re-agree
          
          // Don't reset understandingConsent as it should auto-fill from profile
          
          // Update auto-filled fields state to show indicators
          setAutoFilledFields(filledFields);
          logger.success('✅ Auto-filling completed', { filledFields: Object.keys(filledFields) });
          
        } else {
          // User is not logged in, start with an empty form
          logger.info('ℹ️ User not logged in - using empty form');
          setName('');
          setEmail('');
          setUnderstandingConsent('');
          setAttendeeType('single');
          setPartnerAlias('');
          setConsentAcknowledgment(false);
          setDataRetentionAgreement(false);
          setPhotoPreview(null);
          setPhotoFile(null);
          setAutoFilledFields({});
        }
      }
    };
    
    attemptAutoFill();
  }, [currentUser, userProfile, authLoading]);
  // Photo handling is managed by the PhotoUpload component

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Validate email
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }    // Validate introduction (optional but if provided, should meet minimum length)
    if (introduction.trim() && introduction.trim().length < 50) {
      newErrors.introduction = 'If providing an introduction, please make it at least 50 characters';
    }

    // Validate consent understanding
    if (!understandingConsent.trim()) {
      newErrors.understandingConsent = 'Please explain your understanding of consent';
    } else if (understandingConsent.trim().length < 50) {
      newErrors.understandingConsent = 'Please provide a more detailed explanation (at least 50 characters)';
    }

    // Validate consent acknowledgment
    if (!consentAcknowledgment) {
      newErrors.consentAcknowledgment = 'You must acknowledge the consent & guidelines';
    }

    // Validate data retention agreement
    if (!dataRetentionAgreement) {
      newErrors.dataRetentionAgreement = 'You must agree to the data retention policy';
    }

    // Validate partner alias if attendee type is couple
    if (attendeeType === 'couple' && !partnerAlias.trim()) {
      newErrors.partnerAlias = 'Partner name/alias is required for couples';
    }

    // Validate photo
    if (!photoPreview) {
      newErrors.photo = 'Please upload a photo for verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent form submission if the form is disabled (email not verified)
    if (disabled) {
      logger.warn('⚠️ Form submission attempted while disabled');
      return;
    }

    if (validateForm()) {
      logger.info('📝 Submitting application form');

      const formData: ApplicationSubmissionData = {
        name,
        email,
        introduction,
        understandingConsent,
        consentAcknowledgment,
        dataRetentionAgreement,
        attendeeType,
        ...(attendeeType === 'couple' ? { partnerAlias } : {}),
        eventId // Include the required eventId
      };

      // If syncWithProfile is true and user is logged in, update profile with these values
      if (syncWithProfile && currentUser && userProfile) {
        try {
          logger.info('🔄 Syncing application data with user profile');
          
          // Prepare update data with fields to sync
          const profileUpdates: Partial<UserProfile> = {};
          
          // Only update consent understanding if it's changed
          if (understandingConsent && understandingConsent !== userProfile.consentUnderstanding) {
            profileUpdates.consentUnderstanding = understandingConsent;
          }
          
          // Only update introduction if it's changed
          if (introduction && introduction !== userProfile.introduction) {
            profileUpdates.introduction = introduction;
          }
          
          // Only update profile if there are changes
          if (Object.keys(profileUpdates).length > 0) {
            await updateExtendedProfile(profileUpdates);
            logger.success('✅ User profile updated with application data');
          }
        } catch (err) {
          // Log error but continue with application submission
          logger.error('❌ Failed to sync application data with profile:', err);
        }
      }

      onSubmit(formData, photoFile || undefined);
    } else {
      logger.error('❌ Form validation failed');

      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement = document.getElementById(firstErrorKey);
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Handle input change to remove auto-filled status
  const handleInputChange = (fieldName: string, value: any) => {
    // Update the field value using the appropriate setter
    switch (fieldName) {
      case 'name':
        setName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      default:
        break;
    }
    
    // If this is a field that was auto-filled, remove that status since the user is editing it
    if (autoFilledFields[fieldName]) {
      setAutoFilledFields(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`app-form ${disabled ? 'app-form-disabled' : ''}`}>
      {disabled && (
        <div className="app-form-disabled-overlay"></div>
      )}
      
      {/* Personal Information Section */}
      <div className="app-form-section">


        <div className="app-form-group">
          <label htmlFor="name">
            First Name * 
            {autoFilledFields.name && (
              <span className="app-auto-filled-indicator">
                <FontAwesomeIcon icon={faCheck} /> Auto-filled
              </span>
            )}
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter only your first name"
            disabled={loading}
            className={autoFilledFields.name ? 'app-auto-filled-input' : ''}
          />
          {errors.name && <div className="app-error-text">{errors.name}</div>}
        </div>
      </div>

      {/* Photo Upload Section */}
      <div className="app-form-section">
        <h3>Photo Verification</h3>
        <div className="app-form-group">
          <label htmlFor="photo">
            Upload a Recent Photo * 
            {autoFilledFields.photo && (
              <span className="app-auto-filled-indicator">
                <FontAwesomeIcon icon={faCheck} /> Auto-filled from your photos
              </span>
            )}
          </label>          <div className={`photo-upload-wrapper ${autoFilledFields.photo ? 'auto-filled-input' : ''}`}>
            <PhotoUpload
              value={photoFile}
              previewUrl={photoPreview}
              onChange={(file, previewUrl) => {
                setPhotoFile(file);
                setPhotoPreview(previewUrl);
                
                // Update auto-filled status when user changes the photo
                if (file && autoFilledFields.photo) {
                  setAutoFilledFields(prev => ({
                    ...prev,
                    photo: false
                  }));
                }
              }}
              disabled={loading}
              label="Select Photo"
              helperText={autoFilledFields.photo ? "Auto-filled from your photos" : ""}
              className={autoFilledFields.photo ? "auto-filled-photo" : ""}
              maxSizeMB={5}
            />
          </div>
          {photoPrefillError && <div className="app-error-text">{photoPrefillError}</div>}
          {errors.photo && <div className="app-error-text">{errors.photo}</div>}
          <div className="app-helper-text">
            Please upload a recent photo of yourself for event verification. This photo will only be used by event organizers for security purposes.
          </div>
        </div>
      </div>

      {/* Introduction Section */}      
      <div className="app-form-section">
        <h3>Introduction</h3>
        <div className="app-form-group">
          <label htmlFor="introduction">
            {autoFilledFields.introduction && (
              <span className="app-auto-filled-indicator">
                <FontAwesomeIcon icon={faCheck} /> Auto-filled from profile
              </span>
            )}
          </label>          <textarea
            id="introduction"
            rows={6}
            value={introduction}
            onChange={(e) => {
              setIntroduction(e.target.value);
              // When user edits, remove auto-filled status
              if (autoFilledFields.introduction) {
                setAutoFilledFields(prev => ({
                  ...prev,
                  introduction: false
                }));
              }
            }}
            placeholder="Tell us about yourself. Share your interests, experience level, what brought you here..."
            disabled={loading}
            className={autoFilledFields.introduction ? 'app-auto-filled-input' : ''}
          />          <div className="app-char-counter">
            {introduction.length} / 50 characters minimum
            {introduction.length < 50 && introduction.length > 0 && (
              <span className="app-char-counter-warning"></span>
            )}
            {introduction.length >= 50 && (
              <span className="app-char-counter-success"> ✓</span>
            )}
          </div>
          <div className="app-sync-option">
            <label className="app-checkbox-label">
              <div className="app-checkbox-container">
                <input
                  type="checkbox"
                  checked={syncWithProfile}
                  onChange={(e) => setSyncWithProfile(e.target.checked)}
                  disabled={loading}
                />
                <span>
                  Update my profile with this introduction when submitting
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Consent Understanding Section */}      <div className="app-form-section">
        <h3>Consent Understanding</h3>
        <div className="app-form-group">
          <label htmlFor="understandingConsent">
            {autoFilledFields.understandingConsent && (
              <span className="app-auto-filled-indicator">
                <FontAwesomeIcon icon={faCheck} /> Auto-filled from profile
              </span>
            )}
          </label>          <textarea
            id="understandingConsent"
            rows={6}
            value={understandingConsent}
            onChange={(e) => {
              setUnderstandingConsent(e.target.value);
              // When user edits, remove auto-filled status
              if (autoFilledFields.understandingConsent) {
                setAutoFilledFields(prev => ({
                  ...prev,
                  understandingConsent: false
                }));
              }
            }}
            placeholder="In your own words, please explain what consent means to you and how it applies to intimate settings..."
            disabled={loading}
            className={autoFilledFields.understandingConsent ? 'app-auto-filled-input' : ''}
          />          <div className="app-char-counter">
            {understandingConsent.length} / 50 characters minimum
            {understandingConsent.length < 50 && understandingConsent.length > 0 && (
              <span className="app-char-counter-warning"></span>
            )}
            {understandingConsent.length >= 50 && (
              <span className="app-char-counter-success"> ✓</span>
            )}
          </div>
          {errors.understandingConsent && (
            <div className="app-error-text">{errors.understandingConsent}</div>
          )}
          <div className="app-sync-option">
            <label className="app-checkbox-label">
              <div className="app-checkbox-container">
                <input
                  type="checkbox"
                  checked={syncWithProfile}
                  onChange={(e) => setSyncWithProfile(e.target.checked)}
                  disabled={loading}
                />
                <span>
                  Update my profile with this consent understanding when submitting
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
      
      {/* Agreement Section */}
      <div className="app-form-section">
        <h3>Agreements</h3>
        <div className="app-form-group checkbox-group">
            <label className="app-checkbox-label">
              <div className="app-checkbox-container">
                <input
                  type="checkbox"
                  checked={consentAcknowledgment}
                  onChange={(e) => setConsentAcknowledgment(e.target.checked)}
                  disabled={loading}
                />                <span>
                  I have read and understood the <a href="/consent" target="_blank">consent & guidelines</a> and agree to respect all boundaries established during the event. *
                </span>
              </div>
            </label>
            {errors.consentAcknowledgment && (
              <div className="app-error-text">{errors.consentAcknowledgment}</div>
            )}
          </div>
  
          <div className="app-form-group checkbox-group">
            <label className="app-checkbox-label">
              <div className="app-checkbox-container">
                <input
                  type="checkbox"
                  checked={dataRetentionAgreement}
                  onChange={(e) => setDataRetentionAgreement(e.target.checked)}
                  disabled={loading}
                />
                <span>
                  I agree that my application information will be retained for verification purposes, and acknowledge that my data will be handled in accordance with the privacy policy. *
                </span>
              </div>
            </label>
            {errors.dataRetentionAgreement && (
              <div className="app-error-text">{errors.dataRetentionAgreement}</div>
            )}
        </div>
      </div>      {/* Submit Section */}
      <div className="app-form-submit">
        <button
          type="submit"
          className={`app-submit-btn ${disabled ? 'verification-required' : ''}`}
          disabled={loading || disabled}
        >
          {loading ? 'Submitting Application...' : disabled ? (
            <>
              <FontAwesomeIcon icon={faExclamationTriangle} className="icon-left" />
              Email Verification Required
            </>
          ) : 'Submit Application'}
        </button>
        {disabled && (
          <p className="app-verification-note">
            Please verify your email address before applying for events
          </p>
        )}
        <p className="app-required-fields-note">* Required fields</p>
      </div>
    </form>
  );
};

export default ApplicationForm;